// Hook que espera a que las fuentes de la app (iconos Material Symbols +
// tipografías del sistema de diseño) terminen de descargarse antes de dar
// por "renderizable" el contenido. Mientras la fuente de iconos no esté lista,
// los glifos aparecerían como texto crudo (p. ej. "lock", "search") por el
// `display=swap` de Google Fonts; con esto la app puede mostrar un spinner
// global hasta que todo esté listo.
//
// Al terminar añade la clase `fonts-loaded` a <html>, que el CSS usa como red
// de seguridad para ocultar los iconos hasta ese momento (ver styles.css).

import { useEffect, useState } from 'react';

// Fuentes que deben estar listas antes de renderizar iconos/textos sin saltos.
// Se piden tamaños/pesos representativos: el navegador agrupa la carga por
// familia, así que basta una variante por familia para disparar la descarga.
const REQUIRED_FONTS = [
  '24px "Material Symbols Outlined"',
  '700 16px "Syne"',
  '400 14px "JetBrains Mono"',
];

/** Marca <html class="fonts-loaded"> una sola vez. */
function markLoaded() {
  if (typeof document !== 'undefined') {
    document.documentElement.classList.add('fonts-loaded');
  }
}

/**
 * Devuelve `true` cuando las fuentes requeridas están disponibles (o cuando el
 * navegador no soporta la Font Loading API, para no bloquear la app).
 */
export function useFontsReady(): boolean {
  const [ready, setReady] = useState<boolean>(() => {
    // Si ya se marcó antes (navegación interna / remonta), no re-bloquees.
    if (typeof document !== 'undefined' && document.documentElement.classList.contains('fonts-loaded')) {
      return true;
    }
    // Sin Font Loading API: no podemos esperar de forma fiable, asumimos listo.
    return typeof document === 'undefined' || !('fonts' in document);
  });

  useEffect(() => {
    if (ready) {
      markLoaded();
      return;
    }

    let active = true;
    const fontSet = document.fonts;

    // Dispara la carga explícita de cada familia y espera a `ready` (que resuelve
    // cuando no quedan fuentes pendientes). Red de seguridad por si alguna
    // `load` falla: igual continuamos para no dejar la app colgada.
    const loads = REQUIRED_FONTS.map((f) => fontSet.load(f).catch(() => undefined));

    Promise.all([...loads, fontSet.ready])
      .catch(() => undefined)
      .finally(() => {
        if (!active) return;
        markLoaded();
        setReady(true);
      });

    // Tope de seguridad: si por lo que sea la carga nunca resuelve, no dejamos
    // al usuario atrapado en el spinner para siempre.
    const timeout = window.setTimeout(() => {
      if (!active) return;
      markLoaded();
      setReady(true);
    }, 5000);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [ready]);

  return ready;
}
